import { useState, useEffect } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';

const FinancePage = () => {
  const [loading, setLoading] = useState(true);
  const [financeData, setFinanceData] = useState(null);

  // Fetch finance data
  const fetchFinanceData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/api/orders/finance/summary`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setFinanceData(data);
      } else {
        toast.error('Failed to load finance data');
      }
    } catch (error) {
      toast.error('Failed to load finance data');
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchFinanceData();
  }, []);

  // Export to CSV
  const exportToCSV = () => {
    if (!financeData || !financeData.orders || financeData.orders.length === 0) {
      toast.error('No data available for export');
      return;
    }

    try {
      // CSV headers
      const headers = [
        'Order Number',
        'Customer Name',
        'Phone',
        'Order Date',
        'Grand Total (₹)',
        'Advance Received (₹)',
        'Balance Remaining (₹)',
        'Settlement Status'
      ];

      // CSV data rows
      const csvData = financeData.orders.map(order => [
        order.orderNo,
        `"${order.customerName}"`, // Wrap in quotes to handle commas in names
        order.phone,
        new Date(order.date).toLocaleDateString('en-IN'),
        order.grandTotal,
        order.totalAdvance,
        order.balance,
        order.settlementStatus
      ]);

      // Combine headers and data
      const csvContent = [headers, ...csvData]
        .map(row => row.join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `finance_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('📄 Finance report exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-300 border-t-blue-600"></div>
          <span className="text-blue-700 font-medium">Loading finance data...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            💰 Financial Accounting
          </h1>
          <p className="text-gray-600">Settlement reports and financial overview</p>
        </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Total Orders Dispatched</h3>
                    <p className="text-3xl font-bold text-blue-600 mt-2">
                      {financeData?.summary?.totalOrdersDispatched || 0}
                    </p>
                  </div>
                  <div className="text-4xl">📦</div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Revenue Expected</h3>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                      {formatCurrency(financeData?.summary?.totalRevenueExpected || 0)}
                    </p>
                  </div>
                  <div className="text-4xl">💰</div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Advance Received</h3>
                    <p className="text-3xl font-bold text-purple-600 mt-2">
                      {formatCurrency(financeData?.summary?.totalAdvanceReceived || 0)}
                    </p>
                  </div>
                  <div className="text-4xl">💵</div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Remaining to Collect</h3>
                    <p className="text-3xl font-bold text-orange-600 mt-2">
                      {formatCurrency(financeData?.summary?.totalRemainingToCollect || 0)}
                    </p>
                  </div>
                  <div className="text-4xl">⏳</div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Fully Settled Orders</h3>
                    <p className="text-3xl font-bold text-emerald-600 mt-2">
                      {financeData?.summary?.totalFullySettled || 0}
                    </p>
                  </div>
                  <div className="text-4xl">✅</div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Settlement Rate</h3>
                    <p className="text-3xl font-bold text-indigo-600 mt-2">
                      {financeData?.summary?.totalOrdersDispatched > 0
                        ? Math.round((financeData.summary.totalFullySettled / financeData.summary.totalOrdersDispatched) * 100)
                        : 0}%
                    </p>
                  </div>
                  <div className="text-4xl">📊</div>
                </div>
              </div>
            </div>

            {/* Export Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Export Financial Report</h3>
                  <p className="text-gray-600 text-sm">
                    Download detailed settlement report as CSV file
                  </p>
                </div>
                <button
                  onClick={exportToCSV}
                  disabled={!financeData?.orders || financeData.orders.length === 0}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-semibold"
                >
                  📄 Export to CSV
                </button>
              </div>
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Dispatched Orders Summary</h3>
                <p className="text-gray-600 text-sm mt-1">
                  All orders that have been dispatched and their settlement status
                </p>
              </div>

              {financeData?.orders && financeData.orders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Financial Summary
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Settlement Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {financeData.orders.map((order) => (
                        <tr key={order.orderNo} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {order.orderNo}
                              </div>
                              <div className="text-sm text-gray-500">
                                {new Date(order.date).toLocaleDateString('en-IN')}
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {order.customerName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {order.phone}
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 mb-1">
                                Total: {formatCurrency(order.grandTotal)}
                              </div>
                              <div className="text-sm text-gray-600 mb-1">
                                Advance: {formatCurrency(order.totalAdvance)}
                              </div>
                              <div className={`text-sm ${order.balance === 0 ? 'text-green-600 font-medium' : 'text-orange-600'}`}>
                                {order.balance === 0 ? 'Fully Paid' : `Balance: ${formatCurrency(order.balance)}`}
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              order.balance === 0
                                ? 'bg-green-100 text-green-800'
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {order.settlementStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-600">
                  <div className="text-4xl mb-4">📊</div>
                  <p className="text-lg font-medium mb-2">No Dispatched Orders Yet</p>
                  <p className="text-sm">Orders will appear here once they are dispatched from the Orders Management page.</p>
                </div>
              )}
            </div>
      </div>
    </>
  );
};

export default FinancePage;
